import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Receipt, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOverdueAppointments, type OverdueAppointmentItem } from '@/hooks/useOverdueAppointments';

function OverdueRow({
  item,
  onSelect,
}: {
  item: OverdueAppointmentItem;
  onSelect: (item: OverdueAppointmentItem) => void;
}) {
  const { appointment, serviceNames } = item;
  const dateLabel = format(new Date(appointment.start_time), "d 'de' MMMM", { locale: es });
  const serviceSummary = serviceNames.length > 1
    ? `${serviceNames.length} servicios: ${serviceNames.join(', ')}`
    : serviceNames[0] ?? 'Servicio';

  return (
    <button
      onClick={() => onSelect(item)}
      className="w-full rounded-2xl border border-amber-200/80 bg-amber-50/70 px-3.5 py-3.5 text-left shadow-[0_18px_34px_-30px_rgba(146,64,14,0.35)] transition-all hover:border-amber-300 hover:bg-amber-50"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl border border-amber-200 bg-white">
          <Receipt className="h-4.5 w-4.5 text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-snug text-slate-800">
            {appointment.client?.full_name ?? 'Cliente'}
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            {dateLabel}
          </p>
          <p className="mt-1 truncate text-xs leading-5 text-slate-500">
            {serviceSummary}
          </p>
        </div>
      </div>
    </button>
  );
}

export function OverdueAppointmentsBell() {
  const { items, count } = useOverdueAppointments();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  // Sin pendientes: no ensuciar el header con un icono vacio.
  if (count === 0) return null;

  const handleSelect = (item: OverdueAppointmentItem) => {
    setOpen(false);
    navigate('/appointments', { state: { openOverdueAppointment: item.appointment } });
  };

  return (
    <div ref={panelRef} className="relative">
      <button
        onClick={() => setOpen((current) => !current)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/10"
        aria-label="Servicios pendientes por cerrar"
      >
        <Receipt className="h-5 w-5 text-white/75" />
        <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold leading-none text-white">
          {count > 9 ? '9+' : count}
        </span>
      </button>

      {open && (
        <div
          // En mobile no se puede confiar en `right-0` respecto al propio boton:
          // este icono no es el mas a la derecha del header (la campana y el
          // avatar quedan despues), asi que un panel ancho anclado a su borde
          // se corre hacia la izquierda y se sale de la pantalla. `fixed` con
          // margenes de viewport lo garantiza contenido sin importar donde
          // este el boton que lo abre. En desktop (sm+) se mantiene el mismo
          // patron de dropdown anclado que ya usa NotificationBell.
          className="fixed inset-x-3 top-14 z-[200] overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_28px_70px_-28px_rgba(13,62,78,0.45)] sm:absolute sm:inset-x-auto sm:right-0 sm:top-11 sm:w-[24rem] sm:max-w-[calc(100dvw-1.5rem)]"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          <div className="border-b border-slate-200/80 bg-[linear-gradient(180deg,#fffaf0_0%,#ffffff_100%)] px-4 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-amber-800">
                    Servicios pendientes por cerrar
                  </span>
                  <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-100 px-1.5 text-[10px] font-bold text-amber-800">
                    {count}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  Citas de fechas anteriores sin finalizar, cancelar o eliminar.
                </p>
              </div>

              <button
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                aria-label="Cerrar panel de pendientes"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-x-hidden overflow-y-auto overscroll-contain bg-slate-50/60 px-3 py-3">
            <div className={cn('space-y-2')}>
              {items.map((item) => (
                <OverdueRow key={item.appointment.group_id ?? item.appointment.id} item={item} onSelect={handleSelect} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
